import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { PageHeroCard } from "@/components/site/PageHeroCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/i18n/LangContext";
import { guideCards, guideGroups } from "@/data/guides";
import { certificatePathFor } from "@/data/certificateSlugs";
import visitHero from "@/assets/visit-hero.jpg";
import { PageMeta } from "@/components/site/PageMeta";

const pathForTarget = (target: string, lang: "sv" | "en") => {
  if (target.startsWith("/guider/")) {
    if (lang === "sv") return target;
    // /guider/category/slug -> /en/guides/category/slug
    return "/en/guides/" + target.slice("/guider/".length);
  }
  if (target.startsWith("/")) {
    if (lang === "sv") return target;
    const [pathname, query = ""] = target.split("?");
    const map: Record<string, string> = {
      "/starta-arende": "/en/start-case",
      "/foretag/arbetsmiljo": "/en/companies/occupational-health",
      "/sa-fungerar-det": "/en/how-it-works",
      "/infor-besoket": "/en/before-visit",
    };
    const base = map[pathname] ?? `/en${pathname}`;
    return query ? `${base}?${query}` : base;
  }
  return certificatePathFor(target, lang);
};

const Guider = () => {
  const { lang } = useLang();
  const [query, setQuery] = useState("");

  const copy =
    lang === "sv"
      ? {
          eyebrow: "Kunskap inför besöket",
          title: "Guider om läkarintyg",
          subtitle:
            "Läs om blanketter, krav, priser och förberedelser för ditt intyg eller din medicinska kontroll.",
          searchPlaceholder: "Sök i guiderna…",
          searchAria: "Sök guider",
          clearSearch: "Rensa sökning",
          readGuide: "Läs guiden",
          noResults: "Inga guider matchade din sökning.",
          ctaTitle: "Osäker på vilket intyg du behöver?",
          ctaText: "Svara på några frågor så visar vi rätt nästa steg.",
          ctaPrimary: "Starta ärende",
          metaTitle: "Guider om läkarintyg | Intygsläkaren",
          metaDescription:
            "Guider om läkarintyg hos Intygsläkaren — blanketter, krav, priser och förberedelser inför ditt besök.",
        }
      : {
          eyebrow: "Knowledge before your visit",
          title: "Guides to medical certificates",
          subtitle:
            "Read about forms, requirements, prices and how to prepare for your certificate or medical examination.",
          searchPlaceholder: "Search guides…",
          searchAria: "Search guides",
          clearSearch: "Clear search",
          readGuide: "Read the guide",
          noResults: "No guides matched your search.",
          ctaTitle: "Not sure which certificate you need?",
          ctaText: "Answer a few questions and we show the right next step.",
          ctaPrimary: "Start your case",
          metaTitle: "Guides to medical certificates | Intygsläkaren",
          metaDescription:
            "Guides to medical certificates at Intygsläkaren — forms, requirements, prices and how to prepare for your visit.",
        };


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return guideCards;
    return guideCards.filter((card) =>
      [card.title[lang], card.description[lang], card.group[lang]].join(" ").toLowerCase().includes(q),
    );
  }, [lang, query]);

  const groups = useMemo(() => {
    if (query.trim()) {
      return filtered.length
        ? [{ title: lang === "sv" ? "Sökresultat" : "Search results", cards: filtered }]
        : [];
    }
    return guideGroups(lang);
  }, [filtered, lang, query]);

  const startHref = lang === "sv" ? "/starta-arende" : "/en/start-case";

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title={copy.metaTitle} description={copy.metaDescription} />
      <Header />
      <main>
        <PageHeroCard
          imageSrc={visitHero}
          imageAlt={lang === "sv" ? "Guider om läkarintyg" : "Guides to medical certificates"}
          compact
        >
          <div className="animate-fade-up max-w-2xl">
            <span className="inline-flex h-7 items-center justify-center rounded-full bg-white/20 px-3.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
              {copy.eyebrow}
            </span>
            <h1 className="mt-4 text-balance font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {copy.subtitle}
            </p>
          </div>
        </PageHeroCard>

        <section className="py-14 lg:py-20">
          <div className="container-wide">
            <div className="group flex items-center gap-2 rounded-full border border-border/70 bg-white p-1.5 shadow-[0_8px_30px_-12px_hsl(162_52%_8%/0.28)] ring-1 ring-black/[0.03] transition-base focus-within:border-primary/30 focus-within:shadow-[0_12px_36px_-12px_hsl(162_52%_8%/0.35)] focus-within:ring-primary/15">
              <div className="relative min-w-0 flex-1">
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground transition-base group-focus-within:text-primary"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={copy.searchPlaceholder}
                  aria-label={copy.searchAria}
                  className="h-12 w-full border-0 bg-transparent pl-12 pr-10 text-base shadow-none placeholder:text-muted-foreground/80 focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-base hover:bg-muted hover:text-foreground"
                    aria-label={copy.clearSearch}
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>

            {groups.length === 0 ? (
              <p className="mt-10 text-center text-sm text-muted-foreground">{copy.noResults}</p>
            ) : (
              <div className="mt-12 space-y-14">
                {groups.map((group) => (
                  <div key={group.title}>
                    <h2 className="font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                      {group.title}
                    </h2>
                    <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {group.cards.map((card) => (
                        <Link
                          key={card.id}
                          to={pathForTarget(card.target, lang)}
                          className="group/card flex flex-col rounded-2xl border border-border/60 bg-card p-6 shadow-card transition-smooth hover:-translate-y-1 hover:border-primary/30 hover:shadow-elevated sm:rounded-3xl"
                        >
                          <h3 className="font-display text-xl font-semibold leading-tight text-foreground">
                            {card.title[lang]}
                          </h3>
                          <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                            {card.description[lang]}
                          </p>
                          <span className="mt-5 inline-flex items-center text-sm font-medium text-primary">
                            {copy.readGuide}
                            <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover/card:translate-x-0.5" />
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="px-2 pb-16 sm:px-3 lg:px-4 lg:pb-20">
          <div className="relative mx-auto w-full max-w-[96rem] overflow-hidden rounded-2xl bg-primary px-8 py-14 text-primary-foreground shadow-elevated sm:rounded-3xl lg:px-16 lg:py-20">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                {copy.ctaTitle}
              </h2>
              <p className="mt-3 text-base text-primary-foreground/85">{copy.ctaText}</p>
              <div className="mt-7">
                <Button asChild variant="onPrimary" size="xl">
                  <Link to={startHref}>
                    {copy.ctaPrimary}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumbs
          items={[
            { label: lang === "sv" ? "Hem" : "Home", to: lang === "sv" ? "/" : "/en" },
            { label: copy.title },
          ]}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Guider;
